// Layer 3 (Transformation) — the monthly planned/unplanned read for M11.
// Pure projection over the shared maintenance event log.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { addMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { BASIS_END } from '../maintenance/downtime.seed';
import { PLANNED_RATIO_TARGET_PCT, YTD_START } from '../maintenance/downtime-events.seed';
import {
  eventsByKind,
  sumHours,
  type DowntimeEvent,
} from '../maintenance/DowntimeEvents.transform';
import { formatHours } from '../maintenance/Downtime.transform';

// ---------------------------------------------------------------------------
// Planned vs unplanned — how reactive the year was, month by month.
//
// The monthly ratio reads the stoppage log rather than the plan, so it takes
// its rows as an argument like every other event projection in this module.
//
// Two rulings are load-bearing here and neither is visible on the glass:
//
//   (a) A month is the CALENDAR month of the event's start (`start.slice(0, 7)`),
//       not the production day `monthStart()` returns. That helper opens a
//       production day at 18:00 of the previous calendar day; on this log no
//       event starts at or after 18:00 on a month's last day, so the two rules
//       agree on every row. The calendar month is stated as the rule so the two
//       never have to be reconciled by hand.
//   (b) The last row is a PARTIAL month. The window closes at the start of the
//       open production day, so June carries seven days against every other
//       row's full month. A short capsule in the last row is the window's
//       boundary, not a month that went well, and the row's own title says so.
//
// durable: monthly planned/unplanned ratio over a stoppage log (ADR-0012).

export type PlannedRatioMonth = {
  /** `2026-01` — the calendar month this row counts. */
  key: string;
  /** `Jan` — the month's own abbreviation, taken from its first day. */
  label: string;
  planned: number;
  unplanned: number;
  total: number;
  /** planned ÷ total × 100, unrounded; 0 on a month with no hours. */
  plannedPct: number;
  /** total ÷ the heaviest month's total × 100 — the capsule's length. */
  widthPct: number;
  /** planned ÷ total × 100 — the green segment's share of its own capsule,
   *  so the pair always fills the capsule exactly. 0 on a month with no hours. */
  plannedSharePct: number;
  /** unplanned ÷ total × 100 — the red segment's share. */
  unplannedSharePct: number;
  /** `30% · 87.5 h` — the ratio and the month's hours, formatted once here. */
  figure: string;
  /** `26.0 h planned · 61.5 h unplanned`, with `· to 07 Jun` on the partial
   *  month — the row's own tooltip, so the split needs no second line. */
  title: string;
};

export type PlannedRatioRead = {
  planned: number;
  unplanned: number;
  /** planned ÷ (planned + unplanned) × 100, unrounded. */
  plannedPct: number;
  targetPct: number;
  /** `Target 80%` — the marker's label. */
  targetLabel: string;
  /** `25%` — the meter's own value label. */
  valueLabel: string;
  tone: VizTone;
  /** `01 Jan – 07 Jun` — the log's window, the same label the Causes tab's
   *  cards carry, so the two panels cannot name the year differently. */
  windowLabel: string;
  /** The largest month total, the scale every capsule is drawn against. */
  heaviestTotal: number;
  unplannedToPlanned: number;
};

/** The planned share read against its target: at or above it `ok`, within ten
 *  points below it `warn`, further below `bad`. Ten points is the band's width,
 *  not a site threshold — the target itself is the only seeded number. */
export function plannedRatioTone(pct: number, target: number): VizTone {
  if (pct >= target) return 'ok';
  if (pct >= target - 10) return 'warn';
  return 'bad';
}

/** `2026-01` … `2026-06` — every calendar month the window touches, ascending
 *  and contiguous, so a month that carried no stoppage still gets a row rather
 *  than leaving a gap in the axis. */
function monthKeys(from: SeriesTime, to: SeriesTime): string[] {
  const keys: string[] = [];
  let year = Number(from.slice(0, 4));
  let month = Number(from.slice(5, 7));
  const last = to.slice(0, 7);
  for (;;) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    keys.push(key);
    if (key >= last) return keys;
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
}

/** `01 Jan` → `Jan`. The month label is the last three characters of the one
 *  `DD Mon` formatter, so the row and the subtitle cannot spell a month
 *  differently. */
function monthLabel(key: string): string {
  return formatDayMonth(`${key}-01T00:00`).slice(-3);
}

/** The window's close — the last closed production day, the day before the
 *  open day's start. One expression, so the row set, the subtitle and the
 *  partial month's tooltip all cut at the same instant. */
function ratioWindowEnd(): SeriesTime {
  return addMinutes(BASIS_END, -24 * 60);
}

/** One row per calendar month from the window's open to its close, hours
 *  summed by kind, in order. Months are bucketed on the event's own start
 *  month; the heaviest total sets the scale the capsules are drawn against. */
export function plannedRatioMonths(rows: readonly DowntimeEvent[]): PlannedRatioMonth[] {
  const keys = monthKeys(YTD_START, ratioWindowEnd());
  const totals = new Map<string, { planned: number; unplanned: number }>(
    keys.map((key) => [key, { planned: 0, unplanned: 0 }]),
  );
  for (const event of rows) {
    const bucket = totals.get(event.start.slice(0, 7));
    if (!bucket) continue;
    bucket[event.kind] += event.hours;
  }

  const heaviestTotal = keys.reduce((heaviest, key) => {
    const bucket = totals.get(key)!;
    return Math.max(heaviest, bucket.planned + bucket.unplanned);
  }, 0);
  const lastKey = keys[keys.length - 1];
  const partialSuffix = ` · to ${formatDayMonth(ratioWindowEnd())}`;

  return keys.map((key) => {
    const { planned, unplanned } = totals.get(key)!;
    const total = planned + unplanned;
    const plannedPct = total > 0 ? (planned / total) * 100 : 0;
    return {
      key,
      label: monthLabel(key),
      planned,
      unplanned,
      total,
      plannedPct,
      widthPct: heaviestTotal > 0 ? (total / heaviestTotal) * 100 : 0,
      plannedSharePct: total > 0 ? (planned / total) * 100 : 0,
      unplannedSharePct: total > 0 ? (unplanned / total) * 100 : 0,
      figure: `${Math.round(plannedPct)}% · ${formatHours(total)}`,
      title:
        `${formatHours(planned)} planned · ${formatHours(unplanned)} unplanned` +
        (key === lastKey ? partialSuffix : ''),
    };
  });
}

/** The month row a cursor falls in, or the log's last month when the cursor
 *  sits past the window — the row this tab prints for that month, so another
 *  surface quoting the month's planned ratio (the Maintenance landing, P14)
 *  reads the same figure. Undefined only on an empty window. */
export function plannedRatioMonthAt(rows: readonly DowntimeEvent[], cursor: SeriesTime): PlannedRatioMonth | undefined {
  const months = plannedRatioMonths(rows);
  return months.find((row) => row.key === cursor.slice(0, 7)) ?? months[months.length - 1];
}

/** The year's planned share against the target, plus everything the meter and
 *  the panel subtitle need. The target is read referentially from the seed, and
 *  the tone is decoded here so the component reads one field rather than
 *  re-deciding what a ratio means. */
export function plannedRatioRead(rows: readonly DowntimeEvent[]): PlannedRatioRead {
  const byKind = eventsByKind(rows);
  const planned = sumHours(byKind.planned);
  const unplanned = sumHours(byKind.unplanned);
  const total = planned + unplanned;
  const plannedPct = total > 0 ? (planned / total) * 100 : 0;
  const months = plannedRatioMonths(rows);
  const heaviestTotal = months.reduce((heaviest, row) => Math.max(heaviest, row.total), 0);

  return {
    planned,
    unplanned,
    plannedPct,
    targetPct: PLANNED_RATIO_TARGET_PCT,
    targetLabel: `Target ${PLANNED_RATIO_TARGET_PCT}%`,
    valueLabel: `${Math.round(plannedPct)}%`,
    tone: plannedRatioTone(plannedPct, PLANNED_RATIO_TARGET_PCT),
    windowLabel: `${formatDayMonth(YTD_START)} – ${formatDayMonth(ratioWindowEnd())}`,
    heaviestTotal,
    unplannedToPlanned: planned > 0 ? unplanned / planned : 0,
  };
}
